import {
  ApiBlueprint,
  createFrontendPlugin,
  createRouteRef,
  discoveryApiRef,
  fetchApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { bugsyApiRef, BugsyBackendClient } from './api/BugsyApi';
import { bugsyRootPath } from './paths';

const alphaRootRouteRef = createRouteRef();
const alphaAdminRouteRef = createRouteRef();

const bugsyApi = ApiBlueprint.make({
  name: 'bugsy',
  params: defineParams =>
    defineParams({
      api: bugsyApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new BugsyBackendClient({ discoveryApi, fetchApi }),
    }),
});

const BugsyGlyph = (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none">
    <path
      d="M8 6.5h8M12 3.5v3M7 10.5V8.8a2.3 2.3 0 0 1 2.3-2.3h5.4A2.3 2.3 0 0 1 17 8.8v1.7m-9.5 0h9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <rect
      x="6"
      y="10.5"
      width="12"
      height="9.5"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="10" cy="15.2" r="1" fill="currentColor" />
    <circle cx="14" cy="15.2" r="1" fill="currentColor" />
  </svg>
);

// A single page for the whole chat experience. The frontend router (AppRoutes)
// turns this `path` into `/bugsy/*`, so this one PageBlueprint -- one lazy
// `loader`, one mounted component -- covers both `/bugsy` and `/bugsy/:threadId`.
// A second PageBlueprint here (as this used to be) means a second, independent
// lazy-loaded component: navigating between the two then unmounts one and
// mounts the other, aborting whatever generation was still streaming into the
// first. See BugsyPage/index.tsx for the internal routing on the thread-id
// segment that this relies on.
const bugsyPage = PageBlueprint.make({
  name: 'chat',
  params: {
    path: bugsyRootPath,
    routeRef: alphaRootRouteRef,
    title: 'Bugsy',
    icon: BugsyGlyph,
    loader: () =>
      import('./components/BugsyPage').then(m => <m.BugsyPage />),
  },
});

const bugsySyncPage = PageBlueprint.make({
  name: 'sync',
  params: {
    path: '/bugsy/admin',
    routeRef: alphaAdminRouteRef,
    title: 'Bugsy Sync',
    loader: () =>
      import('./components/BugsySyncPanel').then(m => <m.BugsySyncPanel />),
  },
});

const bugsyEntityContent = EntityContentBlueprint.make({
  name: 'bugsy',
  params: {
    path: '/bugsy',
    title: 'Ask Bugsy',
    loader: () =>
      import('./components/EntityBugsyContent').then(m => <m.EntityBugsyContent />),
  },
});

/**
 * Backstage frontend plugin.
 *
 * @alpha
 */
export default createFrontendPlugin({
  pluginId: 'bugsy',
  routes: {
    root: alphaRootRouteRef,
    admin: alphaAdminRouteRef,
  },
  extensions: [bugsyPage, bugsyApi, bugsyEntityContent, bugsySyncPage],
  info: {
    packageJson: () => import('../package.json'),
  },
});
